import {DocumentParentView} from "@dv5/api"
import {layoutStylesService} from "@dv5/components"
import {
    AppMode,
    chooseLayout,
    DocumentPart,
    DvDocument,
    DvLayout,
    getDocumentParts,
    getFirstLayout,
    getLayoutLayers,
    getLeafLayouts,
    getRootLayout,
    getSelectedLayout,
    LayoutContentType,
    stripNoLayers,
    TagFiltered,
    useAppDispatch,
    useAppSelector,
} from '@dv5/domain'
import React, {FC, useEffect, useMemo, useState} from "react"
import {DocumentPartView} from "./panels/DocumentPartView"
import {DocumentDescription} from "./components/DocumentDescription"
import {documentApi} from "./services/documentService"
import './DocumentView.less'
import {DocumentOutline} from './components/DocumentOutline'
import {useDocumentOutline} from './components/useDocumentOutline'
import {useDocumentParents} from './components/useDocumentParents'
import {getOutlineLabel} from './components/documentOutlineHelpers'

const EMPTY_PARTS: DocumentPart[] = []

const parentTypeToContentType = (type: DocumentParentView['type']): LayoutContentType =>
    type === 'CANVAS' ? LayoutContentType.CANVAS : LayoutContentType.VIEW

interface Props {
    document: DvDocument
    zoom: number
    mode: AppMode
    layoutId: string
    filteredTags: Array<TagFiltered>
    showOutline: boolean
    onCloseOutline: () => void
    onNavigateToParent: (contentType: LayoutContentType, contentId: string, layoutId: string) => void
}

export const DocumentView: FC<Props> = ({
                                            document,
                                            zoom,
                                            mode,
                                            layoutId,
                                            filteredTags,
                                            showOutline,
                                            onCloseOutline,
                                            onNavigateToParent,
                                        }) => {
    const dispatch = useAppDispatch()
    const styles = layoutStylesService.getLayoutStyles(zoom)
    const [loaded, setLoaded] = React.useState(false)
    const parts = useAppSelector(getDocumentParts)
    const documentParts = parts[document.id] || EMPTY_PARTS
    const outline = useDocumentOutline(document.id, documentParts, showOutline, zoom, onCloseOutline)
    const {parents, parentsLoaded} = useDocumentParents(document.id, showOutline)
    const leafLayouts = useAppSelector(getLeafLayouts)
    const selectedLayout = useAppSelector(getSelectedLayout)
    const rootLayout = useAppSelector(getRootLayout)
    const layoutLayers = useAppSelector(getLayoutLayers)
    const [currentLayers, setCurrentLayers] = useState<Array<string>>([])

    useEffect(() => {
        if (!loaded) {
            const tagIds = filteredTags.filter(tag => tag.checked).map(tag => tag.tag.id)
            documentApi.getParts(document.id, currentLayers, tagIds)
            documentApi.getDescription(document.id)
            setLoaded(true)
        }
    }, [loaded, currentLayers, filteredTags])
    useEffect(() => {
        setLoaded(false)
    }, [filteredTags])

    useEffect(() => {
        const newLayers = layoutLayers[layoutId] || []
        const changed = newLayers.length !== currentLayers.length
        setCurrentLayers(newLayers)
        if (!changed) {
            for (let i = 0; i < newLayers.length; i++) {
                if (newLayers[i] !== currentLayers[i]) {
                    setLoaded(false)
                    break
                }
            }
        } else {
            setLoaded(false)
        }
    }, [layoutLayers])

    const swapParts = (index: number, down: boolean) => {
        const firstIndex = index
        const secondIndex = down ? index + 1 : index - 1
        documentApi.changePartPositions(document.id, [{
            id: documentParts[firstIndex].id,
            index: documentParts[secondIndex].partIndex,
        }, {
            id: documentParts[secondIndex].id,
            index: documentParts[firstIndex].partIndex,
        }])
    }

    const clonePart = (documentId: string, partId: string) => {
        documentApi.clonePart(
            documentId,
            partId,
            documentApi.getNextPartIndex(parts[document.id] || []),
            'Copy of ',
        )
    }

    const currentTags = useMemo(() => {
        return filteredTags
            .filter(tag => tag.checked)
            .map(tag => tag.tag.id)
    }, [filteredTags])

    const handleChooseLayout = (layout: DvLayout) => {
        dispatch(chooseLayout(layout))
    }

    const handleNavigateToParent = (parent: DocumentParentView) => {
        const targetLayout = selectedLayout ?? (rootLayout ? getFirstLayout(rootLayout) : undefined)
        if (!targetLayout) {
            return
        }
        onNavigateToParent(parentTypeToContentType(parent.type), parent.id, targetLayout.id)
    }

    return (
        <div ref={outline.rootRef} className={`documentView${outline.narrow ? ' documentView--narrow' : ''}`}
             style={outline.outlineStyles}>
            {showOutline && <div className="documentOutlineSlot">
                <DocumentOutline id={`document-outline-${layoutId}`} parts={documentParts}
                                 activePartId={outline.activePartId} onSelect={outline.navigateToPart}
                                 onClose={outline.closeOutline}
                                 parents={parents}
                                 parentsLoaded={parentsLoaded}
                                 onNavigateToParent={handleNavigateToParent}
                                 layouts={leafLayouts}
                                 selectedLayoutId={selectedLayout?.id}
                                 onSelectLayout={handleChooseLayout}/>
            </div>}
            <div className='dvLayoutContent documentViewContent' style={styles}>
                <DocumentDescription documentId={document.id}/>
                <div className='documentParts'>
                    {documentParts.map((part, i) => (
                        <div key={part.id} ref={element => {
                            if (element) outline.partRefs.current.set(part.id, element)
                            else outline.partRefs.current.delete(part.id)
                        }} tabIndex={-1} aria-label={getOutlineLabel(part, i)}
                             className={`documentPartAnchor${outline.highlightedPartId === part.id ? ' documentPartAnchor--highlighted' : ''}`}>
                            <DocumentPartView part={part}
                                              documentId={document.id}
                                              layers={stripNoLayers(currentLayers)}
                                              tags={currentTags}
                                              allTags={filteredTags}
                                              arrangeProps={{
                                                  mode: mode,
                                                  index: i,
                                                  groupCount: -1,
                                                  count: documentParts.length,
                                              }}
                                              onSwap={swapParts}
                                              onClone={clonePart}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
